import { HttpErrorResponse } from '@angular/common/http';

/**
 * Errore dell'API letto dal formato Problem Details (RFC 9457) con il campo `code` stabile
 * (research R-26). L'interfaccia sceglie i messaggi in base a `code`, mai al testo del server,
 * tranne gli errori per campo di `validation_failed`, che il server scrive già in italiano.
 */
export interface ApiProblem {
  readonly status: number;
  readonly code: string | null;
  readonly title: string | null;
  /** Messaggi per campo, con le chiavi in camelCase come i campi dei moduli. */
  readonly errors: Readonly<Record<string, readonly string[]>>;
  /** Secondi d'attesa da `Retry-After`, per `login_locked` e `rate_limited`. */
  readonly retryAfterSeconds: number | null;
  /** La richiesta non ha raggiunto il servizio (rete assente, servizio spento). */
  readonly network: boolean;
}

export type ApiErrorCode =
  | 'validation_failed'
  | 'invalid_credentials'
  | 'email_not_verified'
  | 'login_locked'
  | 'rate_limited'
  | 'session_expired'
  | 'unauthenticated'
  | 'token_invalid'
  | 'legal_version_outdated'
  | 'csrf_invalid'
  | 'site_not_found'
  | 'starting'
  | 'reauthentication_required'
  | 'password_compromised'
  | 'profile_conflict'
  | 'second_factor_invalid'
  | 'second_factor_locked'
  | 'account_pending_deletion'
  | 'challenge_expired'
  | 'passkey_invalid'
  | 'passkey_limit_reached'
  | 'passkey_not_found'
  | 'two_factor_not_enabled'
  | 'two_factor_already_enabled'
  | 'recovery_in_progress'
  | 'avatar_invalid'
  | 'export_too_soon'
  | 'deletion_blocked'
  | 'email_unavailable'
  | 'site_conflict'
  | 'site_limit_reached'
  | 'site_archived'
  | 'site_over_plan_limit'
  | 'site_action_forbidden'
  | 'role_incompatible'
  | 'role_warning_unacknowledged'
  | 'role_holder_limit'
  | 'image_invalid'
  | 'geocoding_unavailable'
  | 'project_not_found'
  | 'project_conflict'
  | 'site_deleted'
  | 'last_admin_required'
  | 'name_mismatch'
  | 'participant_not_found'
  | 'invitation_not_found'
  | 'invitation_expired'
  | 'invitation_revoked'
  | 'invitation_already_answered'
  | 'invitation_email_mismatch'
  | 'app_not_found'
  | 'app_inactive'
  | 'app_action_forbidden'
  | 'app_read_only'
  | 'app_not_activatable'
  | 'app_already_active'
  | 'app_role_requirement'
  | 'last_app_admin_required'
  | 'app_admin_exists'
  | 'site_exit_blocked'
  | 'catalog_area_not_found'
  | 'catalog_app_not_found'
  | 'catalog_conflict'
  | 'publication_transition_invalid'
  | 'app_module_missing'
  | 'preview_account_not_found';

interface ProblemBody {
  readonly code?: unknown;
  readonly title?: unknown;
  readonly errors?: unknown;
}

/** Le chiavi degli errori per campo diventano camelCase: `Password` e `password` coincidono. */
function camelCase(key: string): string {
  return key.length > 0 ? key.charAt(0).toLowerCase() + key.slice(1) : key;
}

function readErrors(errors: unknown): Record<string, string[]> {
  const result: Record<string, string[]> = {};
  if (errors && typeof errors === 'object') {
    for (const [key, value] of Object.entries(errors as Record<string, unknown>)) {
      const messages = Array.isArray(value)
        ? value.filter((item): item is string => typeof item === 'string')
        : typeof value === 'string'
          ? [value]
          : [];
      if (messages.length > 0) {
        result[camelCase(key)] = messages;
      }
    }
  }
  return result;
}

/** Legge qualunque errore di `HttpClient` come `ApiProblem`. */
export function parseProblem(error: unknown): ApiProblem {
  if (!(error instanceof HttpErrorResponse)) {
    return {
      status: 0,
      code: null,
      title: null,
      errors: {},
      retryAfterSeconds: null,
      network: true,
    };
  }
  let body: ProblemBody | null = null;
  if (error.error && typeof error.error === 'object') {
    body = error.error as ProblemBody;
  } else if (typeof error.error === 'string') {
    try {
      body = JSON.parse(error.error) as ProblemBody;
    } catch {
      body = null;
    }
  }
  const retryAfter = Number(error.headers?.get('Retry-After'));
  return {
    status: error.status,
    code: typeof body?.code === 'string' ? body.code : null,
    title: typeof body?.title === 'string' ? body.title : null,
    errors: readErrors(body?.errors),
    retryAfterSeconds: Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : null,
    network: error.status === 0,
  };
}

/** Il tempo d'attesa in parole: "45 secondi", "1 minuto", "15 minuti". */
export function formatWait(seconds: number | null): string {
  if (!seconds || seconds <= 0) {
    return 'qualche minuto';
  }
  if (seconds < 60) {
    return seconds === 1 ? '1 secondo' : `${seconds} secondi`;
  }
  const minutes = Math.ceil(seconds / 60);
  return minutes === 1 ? '1 minuto' : `${minutes} minuti`;
}

export const NETWORK_ERROR_MESSAGE =
  'Il servizio non risponde. Controlla la connessione e riprova: i dati inseriti restano nel modulo.';

/** Il messaggio italiano per l'utente, scelto dal codice dell'errore. */
export function problemMessage(problem: ApiProblem): string {
  if (problem.network) {
    return NETWORK_ERROR_MESSAGE;
  }
  switch (problem.code as ApiErrorCode | null) {
    case 'validation_failed':
      return 'Alcuni dati non sono validi: controlla i campi evidenziati.';
    case 'invalid_credentials':
      return 'Email o password non corretti.';
    case 'email_not_verified':
      return "Prima di accedere devi confermare l'email: apri il collegamento che ti abbiamo inviato.";
    case 'login_locked':
      return `Troppi tentativi di accesso non riusciti. Per sicurezza l'accesso è sospeso: riprova tra ${formatWait(problem.retryAfterSeconds)}.`;
    case 'rate_limited':
      return `Troppe richieste in poco tempo. Riprova tra ${formatWait(problem.retryAfterSeconds)}.`;
    case 'session_expired':
      return 'La sessione è scaduta, accedi di nuovo.';
    case 'unauthenticated':
      return 'Per continuare devi accedere.';
    case 'token_invalid':
      return 'Il collegamento non è più valido: è scaduto, è già stato usato oppure ne è stato inviato uno più recente.';
    case 'legal_version_outdated':
      return "Termini d'uso e informativa privacy sono stati aggiornati: rileggili e accettali di nuovo.";
    case 'csrf_invalid':
      return 'La richiesta non è stata accettata per motivi di sicurezza. Ricarica la pagina e riprova.';
    case 'site_not_found':
      return 'Il cantiere non esiste o non è accessibile.';
    case 'starting':
      return 'Il servizio si sta avviando. Riprova tra qualche istante.';
    case 'reauthentication_required':
      return 'Per sicurezza conferma la tua identità prima di continuare.';
    case 'password_compromised':
      return "Questa password compare fra quelle violate in attacchi noti: scegline un'altra.";
    case 'profile_conflict':
      return 'Il profilo è stato modificato altrove dopo che hai aperto la pagina. Ricarica i dati aggiornati prima di salvare.';
    case 'second_factor_invalid':
      return "Il codice non è corretto o è scaduto. Controlla l'app di autenticazione e riprova.";
    case 'second_factor_locked':
      return `Troppi codici errati. Per sicurezza la verifica è sospesa: riprova tra ${formatWait(problem.retryAfterSeconds)}.`;
    case 'account_pending_deletion':
      return "L'account è in attesa di eliminazione: per usarlo di nuovo annulla l'eliminazione.";
    case 'challenge_expired':
      return 'Il tempo per completare la verifica è scaduto. Ricomincia da capo.';
    case 'passkey_invalid':
      return 'La passkey non è stata riconosciuta. Riprova oppure usa un altro metodo.';
    case 'passkey_limit_reached':
      return 'Hai già 10 passkey, il numero massimo: rimuovine una prima di aggiungerne altre.';
    case 'passkey_not_found':
      return 'La passkey non esiste più: forse è stata rimossa da un altro dispositivo.';
    case 'two_factor_not_enabled':
      return 'La verifica in due passaggi non è attiva.';
    case 'two_factor_already_enabled':
      return 'La verifica in due passaggi è già attiva.';
    case 'recovery_in_progress':
      return "C'è già un recupero dell'account in corso: controlla l'email con la data di completamento.";
    case 'avatar_invalid':
      return "Il file non è un'immagine valida. Scegli una foto JPEG, PNG o WEBP fino a 10 MB.";
    case 'export_too_soon':
      return 'Puoi chiedere una nuova copia dei dati una volta ogni 24 ore. Riprova più tardi.';
    case 'deletion_blocked':
      return "Prima di eliminare l'account trasferisci o chiudi i cantieri e le commesse condivisi con altri.";
    case 'email_unavailable':
      return "L'indirizzo non è più disponibile: è stato registrato da un altro account nel frattempo.";
    case 'site_conflict':
      return 'Il cantiere è stato modificato da un altro utente dopo che hai aperto la pagina. Ricarica i dati aggiornati prima di salvare.';
    case 'site_limit_reached':
      return 'Hai raggiunto il numero di cantieri attivi compreso nel tuo piano: archivia un cantiere o scopri i piani.';
    case 'site_archived':
      return 'Il cantiere è archiviato: riattivalo per modificarlo.';
    case 'site_over_plan_limit':
      return 'Il cantiere è oltre il limite del piano ed è in sola lettura.';
    case 'site_action_forbidden':
      return 'Il tuo ruolo nel cantiere non consente questa operazione.';
    case 'role_incompatible':
      return 'Questi ruoli non possono essere della stessa persona nello stesso cantiere.';
    case 'role_warning_unacknowledged':
      return 'Conferma la combinazione di ruoli per continuare.';
    case 'role_holder_limit':
      return 'Il ruolo ha già il suo titolare nel cantiere.';
    case 'image_invalid':
      return "Il file non è un'immagine valida. Scegli una foto JPEG, PNG o WEBP fino a 10 MB.";
    case 'geocoding_unavailable':
      return 'La ricerca degli indirizzi non è disponibile: scrivi l’indirizzo a mano.';
    case 'project_not_found':
      return 'La commessa non esiste o non è accessibile.';
    case 'project_conflict':
      return 'La commessa è stata modificata da un altro utente dopo che hai aperto la pagina. Ricarica i dati aggiornati prima di salvare.';
    case 'site_deleted':
      return 'Il cantiere è stato eliminato.';
    case 'last_admin_required':
      return 'Serve sempre almeno un Amministratore: nomina prima un altro Amministratore.';
    case 'name_mismatch':
      return 'Il nome scritto non corrisponde: controllalo e riprova.';
    case 'participant_not_found':
      return 'Il partecipante non c’è più: ricarica l’elenco.';
    case 'invitation_not_found':
      return 'L’invito non esiste o il collegamento non è completo.';
    case 'invitation_expired':
      return 'L’invito è scaduto: chiedi a chi ti ha invitato di mandarne uno nuovo.';
    case 'invitation_revoked':
      return 'L’invito è stato revocato da chi lo aveva inviato.';
    case 'invitation_already_answered':
      return 'Hai già risposto a questo invito.';
    case 'invitation_email_mismatch':
      return 'L’invito è per un altro indirizzo email: esci e accedi con l’indirizzo giusto.';
    case 'app_not_found':
      return 'L’App non esiste in questo cantiere oppure non hai un ruolo per usarla.';
    case 'app_inactive':
      return 'L’App è disattivata in questo cantiere.';
    case 'app_action_forbidden':
      return 'Il tuo ruolo nell’App non consente questa operazione.';
    case 'app_read_only':
    case 'app_not_activatable':
    case 'site_exit_blocked':
    case 'preview_account_not_found':
      // Il server spiega il motivo (sola lettura, piano, App da passare a un altro Amministratore).
      return problem.title ?? 'L’operazione non è possibile in questo momento.';
    case 'app_already_active':
      return 'L’App è già presente in questo cantiere.';
    case 'app_role_requirement':
      return 'Questo ruolo dell’App richiede un ruolo professionale che la persona non ha nel cantiere.';
    case 'last_app_admin_required':
      return 'L’App deve avere almeno un Amministratore: assegna prima il ruolo a un’altra persona.';
    case 'app_admin_exists':
      return 'L’App ha già un Amministratore: chiedi a lui di assegnare i ruoli.';
    case 'catalog_area_not_found':
      return 'L’area tematica non esiste.';
    case 'catalog_app_not_found':
      return 'L’App non è nel catalogo.';
    case 'catalog_conflict':
      return 'Esiste già un’area con questo codice.';
    case 'publication_transition_invalid':
      return 'Questo passaggio di stato non è ammesso.';
    case 'app_module_missing':
      return 'L’App non è ancora nel rilascio: per ora si può solo annunciare.';
    default:
      if (problem.status === 413) {
        return 'Il file è troppo grande: il limite è 10 MB.';
      }
      return problem.status === 503
        ? 'Il servizio non è disponibile in questo momento. Riprova tra qualche istante.'
        : 'Si è verificato un errore imprevisto. Riprova.';
  }
}

/**
 * Un campo aggiuntivo del corpo Problem Details, per esempio `remainingAttempts` o `blockers`;
 * `undefined` se manca o se l'errore non viene dall'API.
 */
export function problemExtension(error: unknown, key: string): unknown {
  if (!(error instanceof HttpErrorResponse)) {
    return undefined;
  }
  let body: unknown = error.error;
  if (typeof body === 'string') {
    try {
      body = JSON.parse(body) as unknown;
    } catch {
      return undefined;
    }
  }
  return body && typeof body === 'object' ? (body as Record<string, unknown>)[key] : undefined;
}

/** Il primo messaggio d'errore per un campo, se c'è. */
export function fieldError(problem: ApiProblem | null, field: string): string | null {
  return problem?.errors[field]?.[0] ?? null;
}
